import React from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import Popover from "@material-ui/core/Popover";
import Typography from "@material-ui/core/Typography";
import { apiGet } from "../../services/api";
import {
    Card,
    CardContent,
    CardHeader,
    IconButton,
    Badge,
    Button
} from "@material-ui/core";
import { Dictionary } from "../commenting/Comments";
import { useHistory } from "react-router-dom";
import NotificationsNoneIcon from "@material-ui/icons/NotificationsNone";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import Moment from "react-moment";
import { ROUTE_NOTIFICATIONS } from "../../routes";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        typography: {
            padding: theme.spacing(2)
        },
        multiLine: {
            padding: theme.spacing(2),
            whiteSpace: "pre"
        },
        notificationTitle: {
            padding: theme.spacing(2),
            fontWeight: "bold",
            fontSize: 14
        },
        card: {
            backgroundColor: theme.palette.secondary.light,
            borderStyle: "none"
        },
        viewProjectButton: {
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,

            padding: theme.spacing(1, 10, 1, 10),
            fontSize: "1rem",
            "&:hover": {
                backgroundColor: theme.palette.primary.dark
            },
            "&:disabled": {
                backgroundColor: "#bdbdbd",
                color: "#f5f5f5"
            }
        }
    })
);

export interface INotification {
    id: string;
    title: string;
    text: string;
    timestamp: Date;
    actions: Dictionary<any>;
}

export default function Notifications() {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
        null
    );

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const [notifications, setNotifications] = React.useState<INotification[]>(
        []
    );

    React.useEffect(() => {
        const updateNotifications = async () => {
            const url = "users/me/notifications";

            var allNotifications = (await apiGet(url)) as INotification[];

            setNotifications(
                allNotifications.sort(
                    (a, b) =>
                        new Date(b.timestamp).getTime() -
                        new Date(a.timestamp).getTime()
                )
            );
        };

        const interval = setInterval(async () => {
            updateNotifications();
        }, 60000);

        updateNotifications();
        return () => clearInterval(interval);
    }, []);

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    let history = useHistory();

    const view = (notification: INotification) => {
        //// fixes an issue when the notification contains no actions
        if (notification.actions) {
            const url = notification.actions["view"] as string;

            history.push(url.replace(/^(?:\/\/|[^/]+)*\//, "/"));
        }

        handleClose();

        var array = notifications; // make a separate copy of the array
        var index = array.indexOf(notification);

        if (index !== -1) {
            array.splice(index, 1);
            setNotifications(array);
        }

        //// this will mark the notification as read (fire-and-forget.)
        apiGet(`users/me/notifications/${notification.id}`);
    };

    const viewAll = () => {
        history.push(ROUTE_NOTIFICATIONS);
        handleClose();
    };

    return (
        <>
            <IconButton
                onClick={handleClick}
                style={{ padding: 0, marginLeft: "12px" }}
            >
                {notifications.length === 0 ? (
                    <NotificationsNoneIcon />
                ) : (
                    <Badge badgeContent={notifications.length} color="error">
                        <NotificationsActiveIcon color="primary" />
                    </Badge>
                )}
            </IconButton>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right"
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right"
                }}
                getContentAnchorEl={null}
            >
                {notifications.length === 0 && (
                    <div style={{ cursor: "pointer" }} className={classes.card}>
                        <Card
                            className={classes.card}
                            color="primary"
                            variant="outlined"
                            style={{ padding: 0, width: 320, margin: 0 }}
                        >
                            <CardHeader
                                style={{ padding: 0, margin: 3 }}
                                title={
                                    <Typography
                                        variant="body1"
                                        style={{ padding: 0 }}
                                        className={classes.notificationTitle}
                                        gutterBottom
                                    >
                                        No New Notifications
                                    </Typography>
                                }
                            ></CardHeader>
                            <CardContent style={{ padding: 0, margin: 3 }}>
                                <Typography
                                    variant="body2"
                                    style={{ padding: 0 }}
                                    className={classes.typography}
                                    color="textSecondary"
                                    gutterBottom
                                >
                                    Please check back in a few minutes.
                                </Typography>
                            </CardContent>
                        </Card>
                        <hr style={{ margin: 0 }} />
                    </div>
                )}

                {notifications.slice(0, 5).map(n => {
                    return (
                        <div
                            key={n.id}
                            style={{ cursor: "pointer" }}
                            className={classes.card}
                            onClick={() => {
                                view(n);
                            }}
                        >
                            <Card
                                className={classes.card}
                                color="primary"
                                variant="outlined"
                                style={{ padding: 0, width: 320, margin: 0 }}
                            >
                                <CardHeader
                                    style={{ padding: 0, margin: 3 }}
                                    title={
                                        <Typography
                                            variant="body1"
                                            style={{ padding: 0 }}
                                            className={
                                                classes.notificationTitle
                                            }
                                            gutterBottom
                                        >
                                            {n.title}
                                        </Typography>
                                    }
                                ></CardHeader>
                                <CardContent style={{ padding: 0, margin: 3 }}>
                                    <Typography
                                        variant="body2"
                                        style={{ padding: 0 }}
                                        className={classes.multiLine}
                                        color="textSecondary"
                                        gutterBottom
                                    >
                                        {n.text} -{" "}
                                        <Moment fromNow date={n.timestamp} />
                                    </Typography>
                                </CardContent>
                            </Card>
                            <hr style={{ margin: 0 }} />
                        </div>
                    );
                })}

                <Button
                    fullWidth
                    size="small"
                    color="primary"
                    className={classes.viewProjectButton}
                    style={{ width: 310, margin: 3 }}
                    onClick={() => viewAll()}
                >
                    View Notifications
                </Button>
            </Popover>
        </>
    );
}
